import { Tabs } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { usePatient } from 'hooks/api/useUser';
import { CenteredLoading } from 'components/CenteredLoading';
import { BsDisplay, BsFolder, BsInfoCircle, BsJournalText } from 'react-icons/bs';
import { IdNotFoundModal } from 'components/Modal/IdNotFoundModal';
import { PatientDetailProvider } from 'context/PatientDetailContext';
import { PaperWithBorder } from 'components/PaperWithBorder';
import { useShowRestricted } from 'hooks/useShowRestricted';
import { showWorklistFeature } from 'utils/app';
import { Overview } from './Overview/Overview';
import { PatientInfo } from './PatientInfo/PatientInfo';
import { PatientReportsList } from './Reports/PatientReportsList';
import { PatientDetailsTabs } from '../Patients.helpers';
import { PatientDetailsHeader } from './PatientDetailsHeader';
import { PatientDetailsActions } from './PatientDetailsActions';
import { PatientRulesList } from './Rules/PatientRulesList';

export const PatientDetails = () => {
  const { t } = useTranslation();
  const { patientId, tab } = useParams<{ patientId: string; tab: string }>();
  const { data, isLoading } = usePatient({ patientId });
  const { filter } = useShowRestricted();

  const navigate = useNavigate();

  if (isLoading || !tab) return <CenteredLoading />;
  if (!data) return <IdNotFoundModal />;

  return (
    <PatientDetailProvider>
      <PaperWithBorder>
        <Tabs value={tab} onChange={(value) => value && navigate(`/patients/${patientId}/details/${value}${filter}`)}>
          <Tabs.List>
            <Tabs.Tab
              value={PatientDetailsTabs.Overview}
              leftSection={<BsJournalText size={16} />}
              data-testid={`tab-patient-${PatientDetailsTabs.Overview}`}
            >
              {t('patients_overview_tab')}
            </Tabs.Tab>
            <Tabs.Tab
              value={PatientDetailsTabs.Reports}
              leftSection={<BsFolder size={16} />}
              data-testid={`tab-patient-${PatientDetailsTabs.Reports}`}
            >
              {t('patients_reports_tab')}
            </Tabs.Tab>
            <Tabs.Tab
              value={PatientDetailsTabs.Info}
              leftSection={<BsInfoCircle size={16} />}
              data-testid={`tab-patient-${PatientDetailsTabs.Info}`}
            >
              {t('patients_info_tab')}
            </Tabs.Tab>
            {showWorklistFeature && (
              <Tabs.Tab
                value={PatientDetailsTabs.Rules}
                leftSection={<BsDisplay size={16} />}
                data-testid={`tab-patient-${PatientDetailsTabs.Rules}`}
              >
                {t('monitoring_rules_title')}
              </Tabs.Tab>
            )}

            <PatientDetailsActions />
          </Tabs.List>
          <>
            <PatientDetailsHeader />
            <Tabs.Panel value={PatientDetailsTabs.Overview} pt={'xs'}>
              {tab === PatientDetailsTabs.Overview && <Overview />}
            </Tabs.Panel>
            <Tabs.Panel value={PatientDetailsTabs.Reports} pt={'xs'}>
              {tab === PatientDetailsTabs.Reports && <PatientReportsList />}
            </Tabs.Panel>
            <Tabs.Panel value={PatientDetailsTabs.Info} pt={'xs'}>
              {tab === PatientDetailsTabs.Info && <PatientInfo />}
            </Tabs.Panel>
            {showWorklistFeature && (
              <Tabs.Panel value={PatientDetailsTabs.Rules} pt={'xs'}>
                {tab === PatientDetailsTabs.Rules && <PatientRulesList />}
              </Tabs.Panel>
            )}
          </>
        </Tabs>
      </PaperWithBorder>
    </PatientDetailProvider>
  );
};
